import { MarketingBannerVariant } from "@/api/backofficeRest/generated/model"
import {
  IAlertDefinition,
  IAlertsBannersCatalogue,
  IBannerGroup,
  IBannerPlacement,
  IBannerPriorityChange,
  IBannerRow,
  IMarketingBanner,
  IRankedBanner,
} from "./alertsBanners.types"
import {
  ALWAYS_SHOWN_BANNERS,
  FLAG_CONTROLLED_BANNERS,
  VARIANT_ORDER,
  VARIANT_TAB_LABELS,
} from "./marketingBannerConfig"

const NOT_IN_PRIORITY_LIST = -1

// Placements the apps render, in page order. Any other tag found in the data
// gets its own section after these, so a new placement is never silently hidden.
const KNOWN_PLACEMENTS: { tag: string; label: string }[] = [
  { tag: "home", label: "Home" },
  { tag: "usage", label: "Usage" },
]

const normalise = (query?: string) => (query ?? "").trim().toLowerCase()

const matchesAny = (query: string, fields: string[]) => {
  const needle = normalise(query)
  return !needle || fields.some((f) => f.toLowerCase().includes(needle))
}

const capitalise = (tag: string) => tag.charAt(0).toUpperCase() + tag.slice(1)

export class MarketingBanner implements IMarketingBanner {
  constructor(
    public id: number | null,
    public enabled: boolean | null,
    public variant: MarketingBannerVariant | null,
    public name: string,
    public variantKey: string,
    public title: string,
    public body: string,
    public buttonText: string | null,
    public buttonLink: string | null,
    public illustrationName: string,
    public tags: string[],
    public platforms: string[],
    private readonly priorities: Map<string, number>,
  ) {}

  get storedPriority(): number | null {
    return this.id === null || !this.tags.length
      ? null
      : (this.priorities.get(this.tags[0]) ?? null)
  }

  get buttonHref(): string | null {
    return this.buttonLink && /^https?:\/\//i.test(this.buttonLink)
      ? this.buttonLink
      : null
  }

  priorityFor(tag: string): number {
    return this.priorities.get(tag) ?? NOT_IN_PRIORITY_LIST
  }

  matches(query: string): boolean {
    return matchesAny(query, [this.title, this.body, this.name])
  }
}

const variantRank = (banner: IMarketingBanner) =>
  banner.variant === null
    ? VARIANT_ORDER.length
    : VARIANT_ORDER.indexOf(banner.variant)

export class BannerGroup implements IBannerGroup {
  rows: IMarketingBanner[]

  constructor(
    public name: string,
    rows: IMarketingBanner[],
  ) {
    this.rows = [...rows].sort((a, b) => variantRank(a) - variantRank(b))
  }

  get primary(): IMarketingBanner {
    return this.rows[0]
  }

  get rowIds(): number[] {
    return this.rows.flatMap((row) => (row.id === null ? [] : [row.id]))
  }

  get enabled(): boolean {
    return this.rows.some((row) => row.enabled)
  }

  get priority(): number {
    return this.primary.storedPriority ?? 0
  }

  get rowsDisagree(): boolean {
    return this.rows.some(
      (row) =>
        row.enabled !== this.primary.enabled ||
        row.storedPriority !== this.primary.storedPriority,
    )
  }

  get isAlwaysShown(): boolean {
    return ALWAYS_SHOWN_BANNERS.has(this.name)
  }

  get isFlagControlled(): boolean {
    return FLAG_CONTROLLED_BANNERS.has(this.name)
  }

  get versionLabels(): string[] {
    return this.rows.flatMap((row) =>
      row.variant ? [VARIANT_TAB_LABELS[row.variant]] : [],
    )
  }

  matches(query: string): boolean {
    return this.rows.some((row) => row.matches(query))
  }
}

// Renumbers 0, 1, 2… so no two banners tie: the backoffice and portal break ties differently.
export const movedPriorities = (
  order: IBannerPriorityChange[],
  name: string,
  to: number,
): IBannerPriorityChange[] => {
  const from = order.findIndex((entry) => entry.name === name)
  const target = Math.min(Math.max(to, 0), order.length - 1)
  if (from === -1 || target === from) return []
  const moved = order.filter((entry) => entry.name !== name)
  moved.splice(target, 0, order[from])
  return moved.flatMap((entry, priority) =>
    entry.priority === priority ? [] : [{ name: entry.name, priority }],
  )
}

export class AlertDefinition implements IAlertDefinition {
  constructor(
    public name: string,
    public severity: string,
    public title: string,
    public body: string,
    public isDismissible: boolean,
    public tags: string[],
    public priority: number | null,
    public buttonText: string | null,
    public buttonLink: string | null,
    public actionTag: string | null,
  ) {}

  // 1-based rank in the order the portal shows alerts; set by the catalogue
  position = 0

  get inPriorityList(): boolean {
    return this.priority !== null && this.priority !== NOT_IN_PRIORITY_LIST
  }

  matches(query: string): boolean {
    return matchesAny(query, [this.title, this.body, this.name])
  }
}

// Mirrors the portal gateway's AlertsService: severity first, then the
// priority-list index as-is (so an unlisted -1 ranks first within its severity)
const SEVERITY_ORDER = ["critical", "warning", "info", "success"]

const severityRank = (severity: string) => {
  const rank = SEVERITY_ORDER.indexOf(severity.toLowerCase())
  return rank === -1 ? SEVERITY_ORDER.length : rank
}

const byPortalOrder = (a: IAlertDefinition, b: IAlertDefinition) =>
  severityRank(a.severity) - severityRank(b.severity) ||
  (a.priority ?? Infinity) - (b.priority ?? Infinity)

export class AlertsBannersCatalogue implements IAlertsBannersCatalogue {
  alerts: IAlertDefinition[]

  constructor(
    public banners: IMarketingBanner[],
    alerts: IAlertDefinition[],
  ) {
    this.alerts = [...alerts].sort(byPortalOrder)
    this.alerts.forEach((alert, index) => {
      alert.position = index + 1
    })
  }

  get editable(): boolean {
    return this.banners.length > 0 && this.banners.every((b) => b.id !== null)
  }

  placements(query?: string): IBannerPlacement[] {
    const known = KNOWN_PLACEMENTS.map((p) => p.tag)
    const extra = [...new Set(this.banners.flatMap((b) => b.tags))]
      .filter((tag) => !known.includes(tag))
      .map((tag) => ({ tag, label: capitalise(tag) }))

    return [...KNOWN_PLACEMENTS, ...extra].map(({ tag, label }) =>
      this.placement(tag, label, query ?? ""),
    )
  }

  filteredAlerts(query?: string): IAlertDefinition[] {
    return this.alerts.filter((a) => a.matches(query ?? ""))
  }

  private placement(tag: string, label: string, query: string) {
    const tagged = this.banners.filter(
      (b) => b.tags.includes(tag) && b.matches(query),
    )
    const ranked: IRankedBanner[] = tagged
      .filter((b) => b.priorityFor(tag) !== NOT_IN_PRIORITY_LIST)
      .sort(
        (a, b) =>
          a.priorityFor(tag) - b.priorityFor(tag) ||
          a.variantKey.localeCompare(b.variantKey),
      )
      .map((banner) => ({
        variantKey: banner.variantKey,
        position: banner.priorityFor(tag) + 1,
        banner,
      }))
    const unranked: IBannerRow[] = tagged
      .filter((b) => b.priorityFor(tag) === NOT_IN_PRIORITY_LIST)
      .sort((a, b) => a.variantKey.localeCompare(b.variantKey))
      .map((banner) => ({
        variantKey: banner.variantKey,
        position: null,
        banner,
      }))

    return { tag, label, ranked, unranked, groups: this.groups(tag) }
  }

  private groups(tag: string): IBannerGroup[] {
    const byName = new Map<string, IMarketingBanner[]>()
    this.banners
      .filter((b) => b.tags.includes(tag))
      .forEach((b) => byName.set(b.name, [...(byName.get(b.name) ?? []), b]))
    return [...byName].map(([name, rows]) => new BannerGroup(name, rows))
  }
}
